// src/components/settings/EInvoiceSettings.jsx

import React, { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance";

const EInvoiceSettings = ({ clientId }) => {
  const [loading, setLoading] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);

  const [settings, setSettings] = useState({
    supplier_gstin: "",
    einv_username: "",
    einv_password: "",
    api_client_id: "",
    api_client_secret: "",
    registered_ip: "",
    irp_api_url: "https://einvoiceapi.nic.in",
    auto_generate_irn: false,
    auto_ewaybill: false,
  });

  // ============================================================
  // LOAD SETTINGS
  // ============================================================

  useEffect(() => {
    if (!clientId) return;

    const loadSettings = async () => {
      try {
        setLoading(true);

        const response = await axiosInstance.get(
          "/api/einvoice/settings",
          {
            params: {
              client_id: clientId,
            },
          }
        );

        if (response.data) {
          setSettings((prev) => ({
            ...prev,
            ...response.data,
          }));
        }
      } catch (error) {
        console.error(
          "❌ Error loading e-Invoice settings:",
          error?.response?.data || error
        );
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [clientId]);

  // ============================================================
  // HANDLE CHANGE
  // ============================================================

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ============================================================
  // SAVE SETTINGS
  // ============================================================

  const handleSave = async (e) => {
    e.preventDefault();

    if (!clientId) {
      alert("Client ID is missing.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        ...settings,
        client_id: clientId,
      };

      console.log("📤 Saving e-Invoice settings:", payload);

      const response = await axiosInstance.post(
        "/api/einvoice/settings/save",
        payload
      );

      console.log(
        "📥 e-Invoice save response:",
        response.data
      );

      alert(
        response.data?.message ||
          "e-Invoice settings saved successfully."
      );
    } catch (error) {
      console.error(
        "❌ Error saving e-Invoice settings:",
        error?.response?.data || error
      );

      alert(
        "Save failed: " +
          (
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error.message
          )
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // GENERATE AUTH TOKEN
  // ============================================================

  const handleGenerateToken = async () => {
    if (!clientId) {
      alert("Client ID is missing.");
      return;
    }

    try {
      setTokenLoading(true);

      console.log(
        "🔐 Generating e-Invoice authentication token..."
      );

      const response = await axiosInstance.post(
        "/api/einvoice/auth/token",
        {
          client_id: clientId,
        }
      );

      console.log(
        "📥 Token response:",
        response.data
      );

      alert(
        response.data?.message ||
          "Authentication token generated successfully."
      );

      // Reload settings after token generation
      const settingsResponse = await axiosInstance.get(
        "/api/einvoice/settings",
        {
          params: {
            client_id: clientId,
          },
        }
      );

      if (settingsResponse.data) {
        setSettings((prev) => ({
          ...prev,
          ...settingsResponse.data,
        }));
      }
    } catch (error) {
      console.error(
        "❌ Token generation failed:",
        error?.response?.data || error
      );

      alert(
        "Token generation failed: " +
          (
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error.message
          )
      );
    } finally {
      setTokenLoading(false);
    }
  };

  // ============================================================
  // OPEN EXTERNAL LINK
  // ============================================================

  const openLink = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // ============================================================
  // INPUT CLASSES
  // ============================================================

  const inputClass =
    "w-full h-9 px-3 text-sm border border-gray-300 rounded-md " +
    "bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 " +
    "focus:border-blue-500";

  const labelClass =
    "block text-xs font-medium text-gray-600 mb-1";

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="w-full max-w-5xl mx-auto px-4 py-3">
      <form onSubmit={handleSave}>

        {/* ======================================================
            HEADER
        ====================================================== */}

        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-800">
            e-Invoice
          </h2>

          <p className="text-xs text-gray-500 mt-0.5">
            Configure e-Invoice credentials, IRP connection and
            automatic IRN generation.
          </p>
        </div>

        {/* ======================================================
            REGISTRATION & QUICK LINKS
        ====================================================== */}

        <div className="bg-white border border-blue-200 rounded-lg mb-4">

          <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-200">
            <h3 className="text-sm font-semibold text-gray-700">
              e-Invoice Registration & Quick Links
            </h3>

            <p className="text-[11px] text-gray-500 mt-0.5">
              Use the official portals below to register, check
              e-Invoice status and manage API access.
            </p>
          </div>

          <div className="p-3">

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">

              {/* GST PORTAL */}

              <button
                type="button"
                onClick={() =>
                  openLink("https://www.gst.gov.in/")
                }
                className="flex items-center justify-between
                  h-10 px-3 rounded-md border border-gray-200
                  bg-white hover:bg-gray-50
                  text-left transition"
              >
                <div>
                  <div className="text-xs font-semibold text-gray-700">
                    GST Portal
                  </div>

                  <div className="text-[10px] text-gray-400">
                    GST login & services
                  </div>
                </div>

                <span className="text-blue-600 text-sm">
                  ↗
                </span>
              </button>

              {/* GST E-INVOICE PORTAL */}

              <button
                type="button"
                onClick={() =>
                  openLink("https://einvoice.gst.gov.in/")
                }
                className="flex items-center justify-between
                  h-10 px-3 rounded-md border border-gray-200
                  bg-white hover:bg-gray-50
                  text-left transition"
              >
                <div>
                  <div className="text-xs font-semibold text-gray-700">
                    Enable e-Invoice
                  </div>

                  <div className="text-[10px] text-gray-400">
                    GST e-Invoice portal
                  </div>
                </div>

                <span className="text-blue-600 text-sm">
                  ↗
                </span>
              </button>

              {/* IRIS PORTAL */}

              <button
                type="button"
                onClick={() =>
                  openLink("https://einvoice6.gst.gov.in/")
                }
                className="flex items-center justify-between
                  h-10 px-3 rounded-md border border-gray-200
                  bg-white hover:bg-gray-50
                  text-left transition"
              >
                <div>
                  <div className="text-xs font-semibold text-gray-700">
                    IRIS IRP Portal
                  </div>

                  <div className="text-[10px] text-gray-400">
                    IRP login & registration
                  </div>
                </div>

                <span className="text-blue-600 text-sm">
                  ↗
                </span>
              </button>

              {/* API REGISTRATION */}

              <button
                type="button"
                onClick={() =>
                  openLink(
                    "https://einvoice6.gst.gov.in/irp/api/dashboard/api-registration"
                  )
                }
                className="flex items-center justify-between
                  h-10 px-3 rounded-md border border-gray-200
                  bg-white hover:bg-gray-50
                  text-left transition"
              >
                <div>
                  <div className="text-xs font-semibold text-gray-700">
                    API Registration
                  </div>

                  <div className="text-[10px] text-gray-400">
                    IRIS API access
                  </div>
                </div>

                <span className="text-blue-600 text-sm">
                  ↗
                </span>
              </button>

            </div>
          </div>
        </div>

        {/* ======================================================
            E-INVOICE CONFIGURATION
        ====================================================== */}

        <div className="bg-white border border-gray-200 rounded-lg mb-4">

          <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700">
              e-Invoice Configuration
            </h3>
          </div>

          <div className="p-4">

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">

              {/* Supplier GSTIN */}

              <div>
                <label className={labelClass}>
                  Supplier GSTIN
                </label>

                <input
                  type="text"
                  name="supplier_gstin"
                  value={settings.supplier_gstin || ""}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      supplier_gstin:
                        e.target.value.toUpperCase(),
                    }))
                  }
                  maxLength={15}
                  placeholder="29ABCDE1234F1Z5"
                  className={inputClass}
                />

                <p className="text-[11px] text-gray-400 mt-1">
                  GSTIN registered for e-Invoice
                </p>
              </div>

              {/* Registered IP */}

              <div>
                <label className={labelClass}>
                  Registered IP
                </label>

                <input
                  type="text"
                  name="registered_ip"
                  value={settings.registered_ip || ""}
                  onChange={handleChange}
                  placeholder="123.123.123.123"
                  className={inputClass}
                />

                <p className="text-[11px] text-gray-400 mt-1">
                  IP address registered with the IRP provider
                </p>
              </div>

              {/* Username */}

              <div>
                <label className={labelClass}>
                  e-Invoice Username
                </label>

                <input
                  type="text"
                  name="einv_username"
                  value={settings.einv_username || ""}
                  onChange={handleChange}
                  placeholder="Enter username"
                  className={inputClass}
                  autoComplete="off"
                />
              </div>

              {/* Password */}

              <div>
                <label className={labelClass}>
                  e-Invoice Password
                </label>

                <input
                  type="password"
                  name="einv_password"
                  value={settings.einv_password || ""}
                  onChange={handleChange}
                  placeholder="Enter password"
                  className={inputClass}
                  autoComplete="new-password"
                />
              </div>

              {/* API Client ID */}

              <div>
                <label className={labelClass}>
                  API Client ID
                </label>

                <input
                  type="text"
                  name="api_client_id"
                  value={settings.api_client_id || ""}
                  onChange={handleChange}
                  placeholder="Enter API Client ID"
                  className={inputClass}
                  autoComplete="off"
                />
              </div>

              {/* API Client Secret */}

              <div>
                <label className={labelClass}>
                  API Client Secret
                </label>

                <input
                  type="password"
                  name="api_client_secret"
                  value={settings.api_client_secret || ""}
                  onChange={handleChange}
                  placeholder="Enter API Client Secret"
                  className={inputClass}
                  autoComplete="new-password"
                />
              </div>

              {/* IRP URL */}

              <div className="md:col-span-2">
                <label className={labelClass}>
                  IRP API URL
                </label>

                <input
                  type="text"
                  name="irp_api_url"
                  value={settings.irp_api_url || ""}
                  onChange={handleChange}
                  placeholder="https://einvoiceapi.nic.in"
                  className={inputClass}
                />

                <p className="text-[11px] text-gray-400 mt-1">
                  Invoice Registration Portal API endpoint
                </p>
              </div>

            </div>
          </div>
        </div>

        {/* ======================================================
            AUTOMATION
        ====================================================== */}

        <div className="bg-white border border-gray-200 rounded-lg mb-4">

          <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700">
              Automation
            </h3>
          </div>

          <div className="p-4">

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

              {/* Auto IRN */}

              <label className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-2 cursor-pointer hover:bg-gray-50">

                <div>
                  <div className="text-sm font-medium text-gray-700">
                    Auto Generate IRN
                  </div>

                  <div className="text-[11px] text-gray-400">
                    Automatically generate IRN when invoice is
                    generated
                  </div>
                </div>

                <input
                  type="checkbox"
                  name="auto_generate_irn"
                  checked={
                    Boolean(settings.auto_generate_irn)
                  }
                  onChange={handleChange}
                  className="w-4 h-4"
                />

              </label>

              {/* Auto E-Way Bill */}

              <label className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-2 cursor-pointer hover:bg-gray-50">

                <div>
                  <div className="text-sm font-medium text-gray-700">
                    Auto Generate E-Way Bill
                  </div>

                  <div className="text-[11px] text-gray-400">
                    Automatically generate E-Way Bill when
                    applicable
                  </div>
                </div>

                <input
                  type="checkbox"
                  name="auto_ewaybill"
                  checked={
                    Boolean(settings.auto_ewaybill)
                  }
                  onChange={handleChange}
                  className="w-4 h-4"
                />

              </label>

            </div>
          </div>
        </div>

        {/* ======================================================
            ACTIONS
        ====================================================== */}

        <div className="flex flex-col sm:flex-row justify-end gap-2">

          <button
            type="button"
            onClick={handleGenerateToken}
            disabled={loading || tokenLoading}
            className="h-9 px-4 text-sm font-medium rounded-md
              border border-blue-600 text-blue-600 bg-white
              hover:bg-blue-50 disabled:opacity-50
              disabled:cursor-not-allowed"
          >
            {tokenLoading
              ? "Generating..."
              : "Generate Auth Token"}
          </button>

          <button
            type="submit"
            disabled={loading || tokenLoading}
            className="h-9 px-5 text-sm font-medium rounded-md
              bg-blue-600 text-white hover:bg-blue-700
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Saving..." : "Save Changes"}
          </button>

        </div>

      </form>
    </div>
  );
};

export default EInvoiceSettings;